<template>
    <v-row>
        <v-col cols="12" md="6">
            <div
                v-if="product.main_image"
                class="gallery d-flex flex-wrap flex-md-nowrap"
                :class="{ 'is-modal': isModal }"
            >
                <div
                    v-show="otherImagesCount"
                    class="gallery-thumbs mt-2 mr-md-2 mt-md-0 order-1 order-md-0 w-100"
                    :style="{ height: $vuetify.display.smAndDown ? 'auto' : `${width}px` }"
                >
                    <swiper
                        :modules="[Thumbs]"
                        :direction="
                            $vuetify.display.smAndDown || isModal ? 'horizontal' : 'vertical'
                        "
                        :space-between="10"
                        :breakpoints="isModal ? undefined : breakpoints"
                        watch-slides-progress
                        slides-per-view="3.75"
                        :class="$vuetify.display.smAndDown || isModal ? '' : 'h-100'"
                        @swiper="swiperThumbs = $event"
                    >
                        <swiper-slide
                            class="cursor-pointer"
                            :class="$vuetify.display.smAndDown || isModal ? '' : 'h-auto'"
                        >
                            <v-img aspect-ratio="1" cover
                                   class="bg-white"
                                   :src="selectedVariant?.main_image?.url ? selectedVariant.main_image?.url : product.main_image?.variants?.[0]?.url || product.main_image?.url"
                                   :alt="selectedVariant?.main_image?.alt ? selectedVariant.main_image?.alt : product.main_image?.alt"
                            ></v-img>
                        </swiper-slide>
                        <swiper-slide v-for="media in (selectedVariant?.other_images ? selectedVariant.other_images : product.other_images)" :key="media.id"
                                      class="cursor-pointer" :class="$vuetify.display.smAndDown || isModal ? '' : 'h-auto'">
                            <v-img class="bg-white" aspect-ratio="1" cover :src="media.variants?.[0]?.url || media.url"
                                   :alt="media.alt"
                            ></v-img>
                        </swiper-slide>
                    </swiper>
                </div>
                <div ref="swiperWrap" class="flex-fill" style="min-width: 0">
                    <swiper
                        :modules="[Thumbs, EffectFade]"
                        :thumbs="{
                            swiper: swiperThumbs,
                            multipleActiveThumbs: false,
                            autoScrollOffset: 1,
                        }"
                        effect="fade"
                        class="flex-fill"
                        @swiper="swiperMain = $event"
                    >
                        <tag :flags="product.flags" :apply-margin="false" />
                        <swiper-slide
                            class="d-flex swiper-no-swiping justify-center swiper-no-swiping bg-white cursor-pointer"
                            @click="gallery = true"
                        >
                            <v-img
                                aspect-ratio="1"
                                :src="selectedVariant?.main_image?.url ? selectedVariant.main_image?.url : product.main_image?.variants?.[0]?.url || product.main_image?.url" width="600"
                                :alt="selectedVariant?.main_image?.alt ? selectedVariant.main_image?.alt : product.main_image?.alt"
                                class="flex-grow-0 main-image bg-white"
                            >
                                <div v-if="!isModal" class="d-flex align-end justify-end h-100">
                                    <v-btn
                                        :icon="mdiOverscan"
                                        class="mr-3 mb-3 cart-btn hovering"
                                        rounded="0"
                                        @click="gallery = true"
                                    />
                                </div>
                            </v-img>
                        </swiper-slide>
                        <swiper-slide v-for="media in (selectedVariant?.other_images ? selectedVariant.other_images : product.other_images)" :key="media.id"
                                      class="d-flex swiper-no-swiping">
                            <v-img aspect-ratio="1" cover :src="media.variants?.[0]?.url || media.url" width="600"
                                   :alt="media.alt"
                                   class="flex-fill main-image bg-white">
                                <div v-if="!isModal" class="d-flex align-end justify-end h-100">
                                    <v-btn
                                        :icon="mdiOverscan"
                                        class="mr-3 mb-3 cart-btn hovering"
                                        rounded="0"
                                        @click="gallery = true"
                                    />
                                </div>
                            </v-img>
                        </swiper-slide>
                    </swiper>
                </div>
            </div>
            <div v-else class="position-relative h-100" style="z-index: 1">
                <tag :flags="product.flags" :apply-margin="false" />
            </div>
        </v-col>
        <v-col cols="12" md="6" :class="{'d-flex flex-column justify-center': $vuetify.display.lgAndUp && !$page.props.eshopEnabled}">
            <v-sheet>
                <v-card-title tag="h1" class="pt-0 px-0 pb-4" :class="{'font-weight-bold title-only': !$page.props.eshopEnabled}">
                    {{ selectedVariant?.name || product.name }}
                </v-card-title>
                <template v-if="$page.props.eshopEnabled">
                    <v-card-text v-if="!product.is_variant && (!selectedVariant?.price || selectedVariant?.price === product.price)" class="to-warning__static text-h5 px-0">
                        <template v-if="hasActionPrice">
                            <div class="d-flex flex-column">
                                <div>{{ selectedVariant?.price || product.price }}</div>
                                <div class="text-subtitle-1 text-decoration-line-through">
                                    {{ selectedVariant?.base_price || product.base_price }}
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            {{ selectedVariant?.price || product.price }}
                        </template>
                    </v-card-text>
                    <v-card-text
                        v-if="product.unit_price"
                        class="text-grey text-body-2 px-0 pt-0 mt-n3"
                    >
                        ({{ product.unit_price }})
                    </v-card-text>
                </template>
                <v-card-text v-if="product.manufacturer" class="pt-0 px-0 d-flex flex-wrap ga-3 align-center">
                    <Link :href="product.manufacturer.url" class="text-black to-warning">
                        {{
                            $t('eshop::product-quick-detail.manufacturer:name', {
                                name: product.manufacturer.name,
                            })
                        }}
                    </Link>
                    <div class="flex-grow-1">
                        <Link :href="product.manufacturer.url" class="text-black to-warning">
                            <v-img
                                v-if="product.manufacturer.main_image?.url"
                                :src="product.manufacturer.main_image.url"
                                :alt="product.manufacturer.main_image.alt"
                                height="100"
                                max-width="140"
                                min-width="1"
                            >
                            </v-img>
                        </Link>
                    </div>
                </v-card-text>
                <v-card-text v-if="product.stock_number || selectedVariant?.stock_number" class="pt-0 px-0">
                    {{ $t('eshop::product-quick-detail.stock_number:stock_number', {stock_number: selectedVariant?.stock_number || product.stock_number}) }}
                </v-card-text>
                <v-card-text
                    v-if="product.annotation"
                    class="pt-0 px-0"
                    v-html="product.annotation"
                />
                <v-card-text class="pt-0 px-0">
                    <variant-picker
                        v-if="product.variants.length || !!product.variants.id"
                        :variants="product.variants"
                        :usable-values="usableParameters"
                        :parameters="variantParameters"
                        @variant-found="variantId = $event"
                    />
                    <v-expansion-panels :model-value="variantId ? 1 : 0">
                        <v-expansion-panel
                            elevation="0"
                            :value="1"
                            class="variant-buttons"
                            v-if="!!selectedVariant"
                        >
                            <v-expansion-panel-text>
                                <v-row align="center">
                                    <v-col
                                        v-if="!product.is_variant && selectedVariant.price !== product.price"
                                        cols="12"
                                        class="to-warning__static text-h5"
                                    >
                                        {{ selectedVariant.price }}
                                    </v-col>
                                    <template v-if="!product.info_product">
                                        <v-col class="param-name" v-show="$vuetify.display.smAndUp">
                                            {{ $t('eshop::product-quick-detail.availability') }}
                                        </v-col>
                                        <v-col cols="9">
                                            <template v-if="selectedVariant.on_stock > 0">
                                            <span
                                                class="text-green">{{ $t('eshop::product-quick-detail.on_stock')
                                                }}</span>
                                            </template>
                                            <span v-else class="text-red">
                                                {{ $t('eshop::product-quick-detail.no_stock') }}
                                            </span>
                                        </v-col>
                                    </template>
                                    <v-col v-if="!product.info_product" class="param-name" v-show="$vuetify.display.smAndUp">
                                        {{ $t('eshop::product-quick-detail.amount') }}
                                    </v-col>
                                    <template v-if="!product.info_product">
                                        <v-col cols="9">
                                            <quantity-changer
                                                v-model="toAdd"
                                                :stock="selectedVariant.on_stock"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-btn
                                                v-if="$page.props.eshopEnabled"
                                                v-intersect="onIntersect"
                                                variant="outlined"
                                                class="cart-btn px-8 cart-btn__border hovering"
                                                color="cis-accent-color"
                                                rounded="0"
                                                @click="addToOrder(toAdd)"
                                            >
                                                <v-icon left :icon="mdiCart" />
                                                {{ $t('eshop::product-quick-detail.add_to_cart') }}
                                            </v-btn>
                                        </v-col>
                                    </template>
                                </v-row>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <div
                        v-if="!product.variants.length && !product.is_variant && $page.props.eshopEnabled"
                        class="d-flex align-center"
                    >
                        <v-row align="center">
                            <template v-if="!product.info_product">
                                <v-col class="param-name" v-show="$vuetify.display.smAndUp">
                                    {{ $t('eshop::product-quick-detail.availability') }}
                                </v-col>
                                <v-col cols="9">
                                    <template v-if="product.on_stock > 0">
                                        <span class="text-green">{{
                                            $t('eshop::product-quick-detail.on_stock')
                                        }}</span>
                                    </template>
                                    <span v-else class="text-red">
                                        {{ $t('eshop::product-quick-detail.no_stock') }}
                                    </span>
                                </v-col>
                                <v-col cols="12">
                                    <v-row align="center">
                                        <v-col class="param-name" v-show="$vuetify.display.smAndUp">
                                            {{ $t('eshop::product-quick-detail.amount') }}
                                        </v-col>
                                        <v-col cols="9">
                                            <quantity-changer
                                                v-model="toAdd"
                                                :stock="product.on_stock"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col cols="12">
                                    <v-btn
                                        v-if="$page.props.eshopEnabled"
                                        v-intersect="onIntersect"
                                        variant="outlined"
                                        class="cart-btn px-8 cart-btn__border hovering"
                                        color="cis-accent-color"
                                        rounded="0"
                                        @click="addToOrder(toAdd)"
                                    >
                                        <v-icon left :icon="mdiCart" />
                                        {{ $t('eshop::product-quick-detail.add_to_cart') }}
                                    </v-btn>
                                </v-col>
                            </template>
                        </v-row>
                    </div>
                    <v-divider class="my-4" />
                    <div v-if="product.all_groups.length" class="d-flex flex-wrap align-center">
                        <span class="mr-2"
                            >{{ $t('eshop::product-quick-detail.in_categories') }}:</span
                        >
                        <template v-for="(group, index) in product.all_groups" :key="group.id">
                            <Link :href="group.url" class="to-warning text-black">
                                {{ group.name }}
                            </Link>
                            <template v-if="index !== product.all_groups.length - 1"
                                >,&nbsp;
                            </template>
                        </template>
                    </div>
                    <v-btn
                        variant="outlined"
                        color="cis-accent-color"
                        rounded="0"
                        :prepend-icon="mdiHeadQuestionOutline"
                        :class="{ 'mt-2 mr-2': product.all_groups.length }"
                    >
                        {{ $t('eshop::product-quick-detail.product_inquiry') }}
                        <v-dialog v-model="queryDialog" activator="parent" width="500">
                            <product-query-form
                                :id="product.id"
                                :id_products_variants="selectedVariant?.id"
                                @close-dialog="queryDialog = false"
                            >
                            </product-query-form>
                        </v-dialog>
                    </v-btn>
                    <div
                         class="mt-2">
                        <v-btn
                            variant="outlined"
                            color="cis-accent-color"
                            rounded="0"
                            :prepend-icon="mdiCompareHorizontal"
                            @click="solveComparator()"
                        >
                            {{
                                isComparing
                                    ? $t('eshop::comparator.remove')
                                    : $t('eshop::comparator.add')
                            }}
                            <v-tooltip
                                v-if="comparatorTooltip.message"
                                activator="parent"
                                location="bottom"
                                v-model="comparatorTooltip.model"
                            >
                                {{ comparatorTooltip.message }}
                            </v-tooltip>
                        </v-btn>
                    </div>
                    <div
                        class="mt-2"
                        v-if="$page.props.customer && !Array.isArray($page.props.customer)"
                    >
                        <v-btn
                            variant="outlined"
                            :color="inFavorites ? 'red' : 'cis-accent-color'"
                            rounded="0"
                            :prepend-icon="inFavorites ? mdiHeartOffOutline : mdiHeartOutline"
                            @click="favoritesAction"
                        >
                            <span v-if="inFavorites">
                                {{ $t('eshop::product-quick-detail.remove_from_favorites') }}
                            </span>
                            <span v-else>{{
                                $t('eshop::product-quick-detail.add_to_favorites')
                            }}</span>
                        </v-btn>
                    </div>

                    <div
                        class="mt-2"
                        v-if="$page.props.customer && !Array.isArray($page.props.customer)"
                    >
                        <v-btn
                            v-if="
                                product.is_variant_product ||
                                !!selectedVariant ||
                                !product.variants.length
                            "
                            variant="outlined"
                            color="cis-accent-color"
                            rounded="0"
                            :prepend-icon="mdiViewList"
                            @click="listModalModel = true"
                        >
                            {{ $t('eshop::product-quick-detail.add_to_a_list') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-sheet>
        </v-col>
        <v-col cols="12" v-if="product.description && isModal">
            <div v-html="product.description" />
        </v-col>
        <v-overlay
            width="100%"
            height="100%"
            v-model="gallery"
            v-if="!isModal"
            class="gallery-overlay"
        >
            <image-gallery
                :images="media"
                :index="swiperMain.activeIndex"
                @close="gallery = false"
            />
        </v-overlay>
        <product-list-modal
            v-model="listModalModel"
            :product="product"
            :variant-id="variantId"
        ></product-list-modal>
    </v-row>
</template>

<script setup>
import { computed, defineAsyncComponent, inject, markRaw, ref, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Thumbs, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/effect-fade';
import { useDisplay } from 'vuetify';
import { Link, router, usePage } from '@inertiajs/vue3';
import VariantPicker from './VariantPicker.vue';
import QuantityChanger from './QuantityChanger.vue';
import ImageGallery from './ImageGallery.vue';
import ProductListModal from './ProductListModal.vue';
import Tag from './Tag.vue';
import {
    mdiCart,
    mdiOverscan,
    mdiHeadQuestionOutline,
    mdiCompareHorizontal,
    mdiHeartOutline,
    mdiHeartOffOutline,
    mdiViewList,
} from '@mdi/js';
import { addEvent, generateItem } from '../../../gtm';
import { collect } from '../../../analytics';
import { useCartStore } from '../store/cart';
import pinia from '../../../plugins/pinia';
import axios from 'axios';
import { useComparatorStore } from '../store/comparator.js';
import { useFavorites } from '../composables/favorites.js';
import { useElementSize } from '@vueuse/core';

const cartStore = useCartStore(pinia);
const comparatorStore = useComparatorStore(pinia);
const favorites = useFavorites();

const props = defineProps({
    product: Object,
    addValue: Number,
    isModal: {
        type: Boolean,
        default: true,
    },
    hasActionPrice: {
        type: Boolean,
        default: false,
    },
});

const help = ref(0);
const listModalModel = ref(false);
const productQueryForm = defineAsyncComponent(() => import('./ProductQueryForm.vue'));

const favoritesAction = () => {
    if (inFavorites.value) favorites.removeFromFavorites(props.product.id);
    else favorites.addToFavorites(props.product.id);
};

const inFavorites = computed(() => {
    return (
        usePage().props.favorites &&
        usePage().props.favorites.filter((el) => el.id_products === props.product.id).length > 0
    );
});

const swiperThumbs = ref(null);
const swiperMain = ref(null);
const swiperWrap = ref(null);

const { width } = useElementSize(swiperWrap, { width: 0, height: 0 });

const toAdd = ref(
    props.product.is_variant
        ? props.product.variants.on_stock
            ? 1
            : 0
        : props.product.variants.length
          ? 0
          : props.product.on_stock
            ? 1
            : 0
);
const route = inject('route');
const variantId = ref(
    props.product.is_variant && props.product.variants?.id ? props.product.variants.id : null
);
const selectedVariant = ref(
    props.product.is_variant && props.product.variants?.id ? props.product.variants : null
);
const emit = defineEmits(['selected-variant', 'to-add', 'intersecting']);

const gallery = ref(false);
const queryDialog = ref(false);
const mediaSource = computed(() => {
    if (selectedVariant.value?.id) {
        return selectedVariant.value;
    }
    return props.product;
});
const otherImagesCount = computed(() => {
    return Object.keys(mediaSource.value?.other_images)?.length;
});

const media = computed(() => {
    const media = [];
    if (mediaSource.value.main_image) {
        media.push(mediaSource.value.main_image);
    } else if (props.product.main_image) {
        media.push(props.product.main_image);
    }
    if (otherImagesCount.value) {
        media.push(...Object.values(mediaSource.value.other_images));
    }
    return media;
});

watch(
    () => props.addValue,
    (value) => {
        toAdd.value = value;
    }
);
watch(gallery, (val) => {
    if (val) {
        document.getElementById('root').classList.add('gallery-open');
    } else {
        document.getElementById('root').classList.remove('gallery-open');
    }
});
watch(
    toAdd,
    (value) => {
        emit('to-add', value);
    },
    { immediate: true }
);
watch(
    variantId,
    (val) => {
        if (val) {
            if (Array.isArray(props.product.variants)) {
                const value = props.product.variants.find((item) => item.id === val);
                if (value) {
                    toAdd.value = value.on_stock >= 0 ? 1 : 0;
                }
                selectedVariant.value = value;
            }
        } else {
            selectedVariant.value = null;
            toAdd.value = props.product.is_variant
                ? props.product.variants.on_stock
                    ? 1
                    : 0
                : props.product.variants.length
                    ? 0
                    : props.product.on_stock
                        ? 1
                        : 0;
        }
        help.value++;
        emit('selected-variant', selectedVariant.value);
    },
    { immediate: true }
);

const variantParameters = computed(() => {
    return props.product.product_parameters.reduce((stack, item) => {
        if (item.allow_variants === 'Y') {
            const obj = Object.assign({}, item.parameter);
            obj.values = item.products_parameters_values.reduce((values, value) => {
                values[value.id] = value;
                return values;
            }, {});
            stack[item.id_parameters] = obj;
        }
        return stack;
    }, {});
});
const usableParameters = computed(() => {
    if (Array.isArray(props.product.variants)) {
        return props.product.variants.reduce((filtered, item) => {
            item.options.reduce((stack, option) => {
                if (stack[option.id_parameters]) {
                    if (
                        !stack[option.id_parameters].includes(option.id_products_parameters_values)
                    ) {
                        stack[option.id_parameters].push(option.id_products_parameters_values);
                    }
                } else {
                    stack[option.id_parameters] = [option.id_products_parameters_values];
                }
                return stack;
            }, filtered);
            return filtered;
        }, {});
    }
    return props.product.variants.options.reduce((stack, option) => {
        if (stack[option.id_parameters]) {
            if (!stack[option.id_parameters].includes(option.id_products_parameters_values)) {
                stack[option.id_parameters].push(option.id_products_parameters_values);
            }
        } else {
            stack[option.id_parameters] = [option.id_products_parameters_values];
        }
        return stack;
    }, {});
});

const addToOrder = (count = 1) => {
    // eslint-disable-next-line no-undef
    router.post(
        route('order.addProduct'),
        { id_product: props.product.id, count, id_variant: variantId.value },
        {
            onSuccess: () => {
                const item = cartStore.cartItems.find((item) => {
                    if (variantId.value) {
                        return (
                            item.id_products == props.product.id &&
                            item.id_products_variants == variantId.value
                        );
                    } else {
                        return item.id_products == props.product.id;
                    }
                });
                if (item) {
                    const price = parseFloat(item.nice_price.replace(/\s/g, '').replace(/,/g, '.'));
                    const attributes = {
                        quantity: count,
                        price: price.toFixed(2),
                    };
                    if (variantId.value) {
                        attributes.item_variant = variantId.value;
                    }
                    addEvent({
                        event: 'add_to_cart',
                        ecommerce: {
                            currency: usePage().props.currency,
                            value: (price * count).toFixed(2),
                            items: generateItem(props.product, attributes),
                        },
                    });
                }
            },
        }
    );
};
const { thresholds } = useDisplay();

const breakpoints = markRaw({
    [thresholds.value.md]: {
        slidesPerView: 'auto',
    },
});
if (!props.isModal) {
    watch(useDisplay().smAndDown, (val) => {
        if (val) {
            swiperThumbs.value?.changeDirection('horizontal');
        } else {
            swiperThumbs.value?.changeDirection('vertical');
        }
    });
}

const comparatorTooltip = ref({
    model: false,
    message: '',
});

const isComparing = computed(() => {
    help.value;
    const data = comparatorStore.data.data;
    if (!data.length) return false;
    let variant = undefined;
    if (props.product.is_variant_product) {
        variant = props.product;
    } else if (variantId.value) {
        variant = selectedVariant.value;
    }
    const isVariant = !!variant;
    const found = data.find((item) => {
        return (
            item.type === (isVariant ? 'variant' : 'product') &&
            item.id === (isVariant ? variant.id : props.product.id)
        );
    });
    return !!found;
});

const solveComparator = () => {
    let variant = undefined;
    if (props.product.is_variant_product) {
        variant = props.product;
    } else if (selectedVariant.value) {
        variant = selectedVariant.value;
    }
    const isVariant = !!variant;
    let id, variantId;
    if (isVariant) {
        id = selectedVariant.value ? props.product.id : variant.product.id;
        variantId = selectedVariant.value ? selectedVariant.value.id : variant;
    } else {
        id = props.product.id;
    }
    const add = !isComparing.value;
    const query = [id];
    if (variantId) {
        query.push(variantId);
    }
    const _route = add ? route('comparator.add', query) : route('comparator.remove', query);
    axios[add ? 'put' : 'delete'](_route).then((resp) => {
        comparatorStore.data.data = resp.data.data;
        comparatorStore.data.count = resp.data.count;
        comparatorTooltip.value.model = true;
        comparatorTooltip.value.message = resp.data.message;
    });
};

const onIntersect = (isIntersecting) => {
    if (!props.isModal) {
        emit('intersecting', isIntersecting);
    }
};

if (typeof window !== 'undefined') {
    collect(
        props.product.id,
        'products',
        props.isModal ? 'quick-detail' : 'detail',
        useDisplay().platform.value
    );
    const gtmItem = {
        item_id: props.product.id,
        item_name: props.product.name,
        price: parseFloat(props.product.price.replace(/\s/g, '').replace(/,/g, '.')).toFixed(2),
        quantity: 1,
    };
    if (props.product.manufacturer) {
        gtmItem['item_brand'] = props.product.manufacturer.name;
    }
    if (props.product.all_groups.length) {
        for (let i = 0; i < props.product.all_groups.length; i++) {
            if (i > 5) break;
            gtmItem[`item_category${i !== 0 ? i : ''}`] = props.product.all_groups[i].name;
        }
    }
    addEvent({
        event: 'view_item',
        ecommerce: {
            currency: usePage().props.currency,
            value: gtmItem.price,
            items: [gtmItem],
        },
    });
}
</script>

<style lang="scss">
.plus-minus {
    &.v-btn--disabled {
        box-shadow:
            0 3px 1px -2px rgba(0, 0, 0, 0.2),
            0 2px 2px 0 rgba(0, 0, 0, 0.14),
            0 1px 5px 0 rgba(0, 0, 0, 0.12);
    }
}
</style>

<style scoped lang="scss">
@use 'vuetify/lib/styles/settings/variables' as *;
@use 'sass:map';

.plus-minus {
    min-width: 48px;
}

.gallery {
    &.is-modal {
        flex-wrap: wrap !important;

        div.gallery-thumbs {
            order: 1 !important;
            margin-top: map.get($spacers, 2) !important;
        }
    }
}

.gallery-thumbs {
    flex: 0 0 20%;
    @media #{map.get($display-breakpoints, 'sm-and-down')} {
        flex: 1 1 100%;
    }

    .swiper-vertical {
        margin-bottom: -11px;
    }
}

.variant-buttons {
    ::v-deep(.v-expansion-panel-text__wrapper) {
        padding: 16px 0 0;
    }
}

#main-image {
    flex: 1 1 auto;
}

.cart-btn {
    min-height: 48px;
}

.c-main-swiper {
    :deep(.v-img__img) {
        width: unset;
        max-width: 100%;
        height: auto;
        max-height: 100%;
        left: 50%;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);
    }
}
.title-only {
    font-size: 1.75em;
}
</style>
